import { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { IonSpinner } from "@ionic/react";
import { createCheckoutSession } from "../../lib/stripe/createCheckoutSession";
import {
  trackInitiateCheckout,
  trackViewContent,
} from "../../lib/analytics/metaPixel";
import { signOutUser } from "../../lib/firebase/firebase";
import { isNativeiOS } from "../../lib/utils/platform";
import { hideIntercom, showIntercom } from "../../lib/intercom/intercom";
import branding from "../../config/branding";
import { isSvgTrace } from "../../config/appVariant";
import laptopImage from "../../assets/laptopImage.png";
import "./LifeTimePro.css";

const BENEFITS = isSvgTrace
  ? [
      "Full Access to SVG Editor.",
      "Full Access to Embroidery Editor.",
      "Unlimited AI Enhanced Converts.",
      "Commercial Use Image Generator.",
      "Bulk SVG Conversion.",
      "AI Image Resizer.",
    ]
  : [
      "Full Access to Embroidery Editor.",
      "Convert Images to Stitch Files.",
      "Auto Satin, Fill & Running Stitches.",
      "Unlimited AI-Enhanced Conversions.",
      "3,750 AI credits included every month.",
      "Commercial License Included.",
      "Export DST, PES, JEF & More.",
    ];

const PLANS = isSvgTrace
  ? [
      {
        type: "yearly",
        price: "US$95.88",
        perMonth: "(US$7.99/month)",
        label: "Yearly",
        savings: "Best Value - Save $60",
        subscriptionType: "designerYearly",
      },
      {
        type: "monthly",
        price: "US$12.99",
        perMonth: "per month",
        label: "Monthly",
        subscriptionType: "designer",
      },
    ]
  : [
      {
        type: "yearly",
        price: "US$119.88",
        perMonth: "(US$9.99/month)",
        label: "Yearly",
        savings: "Best Value - Save 45%",
        subscriptionType: "embroideryYearly",
      },
      {
        type: "monthly",
        price: "US$17.99",
        perMonth: "per month",
        label: "Monthly",
        subscriptionType: "embroidery",
      },
    ];

const LifeTimePro = () => {
  const [selectedPlan, setSelectedPlan] = useState("yearly");
  const [loading, setLoading] = useState(false);
  const { user, isLoading: authLoading } = useAuth();

  useEffect(() => {
    trackViewContent("checkout");
    hideIntercom();
    return () => showIntercom();
  }, []);

  const handleUpgrade = async (subscriptionType) => {
    setLoading(true);
    trackInitiateCheckout(subscriptionType);
    try {
      await createCheckoutSession(user.uid, subscriptionType);
    } catch (error) {
      console.error("Error creating checkout session:", error);
      setLoading(false);
    }
  };

  const handleCheckout = () => {
    const plan = PLANS.find((p) => p.type === selectedPlan);
    if (plan) handleUpgrade(plan.subscriptionType);
  };

  const handleLogout = async () => {
    try {
      await signOutUser();
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (authLoading || !user || !user.emailVerified) return null;
  if (isNativeiOS()) return null;

  return (
    <div className="checkout-split">
      <div className="checkout-split__left">
        <div className="checkout-split__left-inner">
          <div className="checkout-split__logo">
            <img src={branding.logo} alt={branding.appName} />
          </div>
          {!isSvgTrace && (
            <div className="checkout-split__hero-text">
              <h1>Unlock Professional Embroidery Tools</h1>
              <p>A full editor for your designs.</p>
            </div>
          )}
          <img
            src={laptopImage}
            alt={branding.proName}
            className="checkout-split__laptop"
          />
        </div>
      </div>

      <div className="checkout-split__right">
        <button className="checkout-split__logout" onClick={handleLogout}>
          Log Out
        </button>

        <h2 className="checkout-split__title">
          {isSvgTrace
            ? `Try ${branding.proName} Free for 5 Days`
            : "Get Your File"}
        </h2>
        <p className="checkout-split__subtitle">
          {isSvgTrace
            ? "Start your free trial today. Cancel anytime during the trial period."
            : "Subscribe to export embroidery files. Cancel anytime."}
        </p>

        <div className="checkout-split__benefits">
          {BENEFITS.map((text) => (
            <div className="checkout-split__benefit" key={text}>
              <span className="checkout-split__benefit-check">✓</span>
              <span className="checkout-split__benefit-text">{text}</span>
            </div>
          ))}
        </div>

        <div className="checkout-split__plans">
          {PLANS.map((plan) => (
            <div
              key={plan.type}
              className={`checkout-split__plan ${selectedPlan === plan.type ? "checkout-split__plan--selected" : ""}`}
              onClick={() => setSelectedPlan(plan.type)}
            >
              <div className="checkout-split__plan-left">
                <div
                  className={`checkout-split__plan-radio ${selectedPlan === plan.type ? "checkout-split__plan-radio--active" : ""}`}
                >
                  {selectedPlan === plan.type && (
                    <div className="checkout-split__plan-radio-dot" />
                  )}
                </div>
                <span className="checkout-split__plan-label">{plan.label}</span>
                {plan.savings && (
                  <span className="checkout-split__plan-savings">
                    {plan.savings}
                  </span>
                )}
              </div>
              <div className="checkout-split__plan-right">
                <span className="checkout-split__plan-price">{plan.price}</span>
                {plan.perMonth && (
                  <span className="checkout-split__plan-permonth">
                    {plan.perMonth}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        <button
          className="checkout-split__cta"
          onClick={handleCheckout}
          disabled={loading}
        >
          {loading ? (
            <IonSpinner name="lines" />
          ) : isSvgTrace ? (
            "Try for Free"
          ) : (
            "Subscribe now"
          )}
        </button>
        <p className="checkout-split__trial-note">
          {isSvgTrace
            ? "No charge for 5 days. Cancel anytime during your trial."
            : "Your selected plan is billed today and renews automatically. Cancel anytime."}
        </p>
      </div>
    </div>
  );
};

export default LifeTimePro;
